/**
 * ShareButton — a toolbar button that opens the ShareModal in a Radix Dialog.
 *
 * Wraps Dialog.Root around both the trigger button and the ShareModal content.
 * Visible to all board roles, keyboard-accessible (Enter/Space), with a visible
 * focus indicator.
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5
 */

import * as React from 'react';
import { Dialog, DialogTrigger } from '../ui/dialog';
import { ShareModal } from './ShareModal';
import type { BoardRole } from './sharing-types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ShareButtonProps {
  readonly boardId: string;
  readonly currentUserId: string;
  readonly currentUserRole: BoardRole;
  readonly onMembershipChanged: () => void;
  /** Optional class name for styling the trigger button to match board toolbar */
  readonly className?: string;
  /** Current board visibility for the visibility selector in ShareModal */
  readonly currentVisibility?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShareButton({
  boardId,
  currentUserId,
  currentUserRole,
  onMembershipChanged,
  className,
  currentVisibility,
}: ShareButtonProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={className}
          aria-label="Share board"
        >
          {/* People/share icon (SVG) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4 inline-block mr-1"
            aria-hidden="true"
          >
            <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
          </svg>
          Share
        </button>
      </DialogTrigger>
      {open && (
        <ShareModal
          boardId={boardId}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          onMembershipChanged={onMembershipChanged}
          currentVisibility={currentVisibility}
        />
      )}
    </Dialog>
  );
}
